import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FOCUS_RING } from "../utils/a11y";

const KEY = "gravisun.consent";

function read() {
  try {
    return localStorage.getItem(KEY);
  } catch (e) {
    return null;
  }
}

function write(value) {
  try {
    localStorage.setItem(KEY, value);
  } catch (e) {
    /* Private mode or blocked storage — the choice just does not persist,
       which is the safe direction: consent stays denied next visit. */
  }
}

/* Analytics is denied by default in index.html's Consent Mode block. This is
 * the ONLY thing that ever grants it.
 *
 * Accept and Decline get the same visual weight — a decline styled as an
 * afterthought is not a real choice. */
export default function CookieConsent() {
  const [choice, setChoice] = useState(() => read());
  /* Renders nothing until mounted. The prerender pass has no localStorage, so
   * without this the banner would be baked into the static HTML and flash for
   * every visitor who had already answered. */
  const [mounted, setMounted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // scripts/prerender.py sets this before the page loads.
    if (window.__PRERENDER__) return;
    setChoice(read());
    setMounted(true);
  }, []);

  /* Announced, but never focus-stealing: this is not a task the visitor came
   * to do, so it must not interrupt the page or trap the keyboard. */
  useEffect(() => {
    if (!mounted || choice) return;
    const id = setTimeout(() => {
      if (ref.current) ref.current.classList.add("is-in");
    }, 600);
    return () => clearTimeout(id);
  }, [mounted, choice]);

  if (!mounted || choice) return null;

  const decide = (granted) => {
    write(granted ? "granted" : "denied");
    if (granted && typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: "granted" });
    }
    setChoice(granted ? "granted" : "denied");
  };

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Cookie choices"
      className="cookie-bar fixed bottom-0 inset-x-0 z-[2000] px-[16px] pb-[16px] pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-[720px] rounded-[20px] bg-[#141317]/95 backdrop-blur-[16px] border border-white/10 shadow-[0_20px_60px_-18px_rgba(0,0,0,0.7)] p-[18px] c-md:p-[22px] flex flex-col c-md:flex-row c-md:items-center gap-[14px] c-md:gap-[20px]">
        <p className="text-14 text-white/75 leading-[1.5] flex-1">
          We'd like to use analytics cookies to understand how the site is used.
          Nothing is loaded until you choose.{" "}
          <Link to="/privacy" className="text-[#F0976B] underline underline-offset-2 hover:text-white">
            Privacy policy
          </Link>
          .
        </p>
        <div className="flex gap-[10px] shrink-0">
          <button
            type="button"
            onClick={() => decide(false)}
            className={`min-h-[44px] px-[18px] rounded-full text-14 font-semibold text-white/85 bg-white/[0.08] border border-white/15 hover:bg-white/[0.14] transition-colors duration-200 ${FOCUS_RING}`}>
            Decline
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            className={`min-h-[44px] px-[18px] rounded-full text-14 font-semibold text-white bg-[#CB4909] hover:bg-[#B33D06] transition-colors duration-200 ${FOCUS_RING}`}>
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
